import {
  Button,
  Stack,
  Typography,
  Modal as MUIModal,
  BoxProps,
  ButtonProps,
  ModalProps,
  Box,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ReactNode } from "react";
import Fade from "@mui/material/Fade";
import Backdrop from "@mui/material/Backdrop";

type TButtonType = "primary" | "secondary" | "danger";

type TModalButton = {
  /**
   * The type of the button, which determines its style and behavior.
   * Can be "primary", "secondary", or "danger".
   */
  type: TButtonType;
  /**
   * The text to be displayed on the button.
   */
  text: string;
  /**
   * Additional props for the button, excluding the "children" prop.
   * This allows customization of the button's appearance and behavior.
   */
  props?: Omit<ButtonProps, "children">;
};

type TModalProps = Omit<ModalProps, "children"> & {
  /**
   * The main heading of the modal.
   */
  heading?: string;
  /**
   * An optional subheading for additional context.
   */
  subheading?: string;
  /**
   * The main body text of the modal.
   */
  body?: string;
  /**
   * An array of buttons to be displayed at the bottom of the modal.
   * Each button can have a type, text, and additional props.
   */
  buttons?: TModalButton[];
  /**
   * Additional props for the container Box component.
   * This can be used to customize the modal's container styling.
   */
  containerProps?: Omit<BoxProps, "children">;
  /**
   * Renders slot for the heading right side.
   */
  headingRightSlot?: ReactNode;
  children?: ReactNode;
  buttonAlignment?: "flex-start" | "center" | "flex-end";
};

const StyledBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  minWidth: 320,
  transform: "translate(-50%, -50%)",
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[10],
  padding: theme.spacing(3),
}));

const getButtonVariant = (type: TButtonType): ButtonProps["variant"] => {
  switch (type) {
    case "primary":
    case "danger":
      return "contained";
    case "secondary":
    default:
      return "text";
  }
};

const getButtonColor = (type: TButtonType): ButtonProps["color"] => {
  switch (type) {
    case "secondary":
      return undefined;
    case "danger":
      return "error";
    case "primary":
    default:
      return "primary";
  }
};

/**
 * A customizable modal component based on MUI Modal component.
 */
export const Modal = ({
  heading,
  subheading,
  body,
  buttons,
  containerProps,
  open,
  headingRightSlot,
  children,
  buttonAlignment = "flex-end",
  ...restProps
}: TModalProps) => {
  return (
    <MUIModal
      closeAfterTransition
      open={open}
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 500 } }}
      {...restProps}
    >
      <Fade in={open}>
        <StyledBox {...containerProps}>
          {heading && (
            <Stack
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <Typography variant="h6" component="h2">
                {heading}
              </Typography>
              {headingRightSlot}
            </Stack>
          )}
          {subheading && (
            <Typography variant="subtitle1" component="p">
              {subheading}
            </Typography>
          )}
          {body && (
            <Typography variant="body1" component="p">
              {body}
            </Typography>
          )}
          {children}
          {buttons && (
            <Stack
              direction="row"
              spacing={1}
              justifyContent={buttonAlignment}
              alignItems="center"
              sx={{ mt: 2 }}
            >
              {buttons.map(({ type, text, props }, index) => (
                <Button
                  key={index}
                  variant={getButtonVariant(type)}
                  color={getButtonColor(type)}
                  size="large"
                  {...props}
                >
                  {text}
                </Button>
              ))}
            </Stack>
          )}
        </StyledBox>
      </Fade>
    </MUIModal>
  );
};

export default Modal;
